import LegCard from '@/components/manage/tickets/LegCard';
import SendTicketsToEmailModal from '@/components/manage/tickets/sendTickets/SendTicketsToEmailModal';
import { manageFlowBookingAdmissionsByIdSelector } from '@/features/manage/manageSelectors';
import { TransText, type TransTextKeys } from '@/i18n/trans/text';
import TicketsDownload from '@/routes/account/tickets/TicketsDownload';
import { useSelector } from '@/store/utils';
import type { BookedTripDTO } from '@/types/dto/agent';
import type { CheckoutOrBookingPassengerMap } from '@/types/passenger';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import { Button, Icons, Typography } from '@packages/webshop-design-system';
import { type FC, useCallback, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

interface TripsByDirectionProps {
  trips: Array<BookedTripDTO>;
  passengersMap: CheckoutOrBookingPassengerMap;
  header: TransTextKeys;
}

const TripsByDirection: FC<TripsByDirectionProps> = ({
  trips,
  header,
  passengersMap,
}) => {
  const [isSendToEmailModalOpen, setIsSendToEmailModalOpen] = useState(false);
  const extractPassengerIds = useCallback((trip: BookedTripDTO): string[] => {
    const passengerIds = (trip.bookedOffers || [])
      .flatMap((offer) => offer.admissions || [])
      .flatMap((admission) => admission.passengerIds || []);

    return [...new Set(passengerIds)].sort();
  }, []);

  const groupTripsByPassenger = useCallback(
    (trips: Array<BookedTripDTO>) => {
      const groupsMap = new Map<
        string,
        { passengerIds: Array<string>; trips: Array<BookedTripDTO> }
      >();

      trips.forEach((trip) => {
        const passengerIds = extractPassengerIds(trip);
        const groupKey = passengerIds.join(',');

        const existingGroup = groupsMap.get(groupKey);
        if (existingGroup) {
          existingGroup.trips.push(trip);
        } else {
          groupsMap.set(groupKey, { passengerIds, trips: [trip] });
        }
      });

      return Array.from(groupsMap.values());
    },
    [extractPassengerIds]
  );

  const admissionsByLegId = useSelector(
    manageFlowBookingAdmissionsByIdSelector
  );
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  const grouped = groupTripsByPassenger(trips);
  const showTripHeadings = grouped.length > 1;

  return (
    <div className="flex flex-col">
      <div
        className={cn(
          'mb-4 flex flex-col-reverse gap-1 laptop:flex-row laptop:items-center laptop:justify-between laptop:gap-4'
        )}
      >
        <Typography
          variant={isLaptopOrBigger ? 'heading2' : 'subtitle'}
          as="h1"
        >
          <TransText i18nKey={header} values={{ count: trips.length }} />
        </Typography>
        {header === 'outboundTrips' && (
          <div className="mb-8 flex gap-2 laptop:mb-0">
            <TicketsDownload />
            <Button
              data-testid="profile-send-to-email-tickets"
              variant="secondary"
              size={isLaptopOrBigger ? 'regular' : 'large'}
              onClick={() => setIsSendToEmailModalOpen(true)}
            >
              <Icons.email height={16} width={16} />
              <TransText i18nKey="sendToEmail" />
            </Button>
          </div>
        )}
      </div>
      {grouped.map((group, groupIdx) => {
        const uniqueODPairs = Array.from(
          new Map(
            group.trips.map((trip) => [
              JSON.stringify([
                trip.originStop?.code || trip.originStop?.name,
                trip.destinationStop?.code || trip.destinationStop?.name,
              ]),
              trip,
            ])
          ).values()
        );
        return (
          <div key={groupIdx} className="mb-6">
            {showTripHeadings && (
              <Typography
                variant={isLaptopOrBigger ? 'subtitle' : 'body1-bold'}
                className="mb-2"
                as="h2"
              >
                <TransText
                  i18nKey="tripNumber"
                  values={{ number: groupIdx + 1 }}
                />
              </Typography>
            )}
            {uniqueODPairs.map((trip, key) => {
              const fulfilledCount = (() => {
                const fulfilledPassengerIds = (trip.bookedOffers || [])
                  .flatMap((offer) => offer.admissions || [])
                  .filter((admission) =>
                    (admission.fulfillments || []).some(
                      (f) => f.status === 'FULFILLED'
                    )
                  )
                  .flatMap((admission) => admission.passengerIds || []);
                return Array.from(new Set(fulfilledPassengerIds)).length;
              })();

              return (
                <div key={key}>
                  <div
                    className={cn(
                      'mb-2 flex items-center gap-1 text-neutral-light'
                    )}
                  >
                    <Typography
                      variant={isLaptopOrBigger ? 'paragraph' : 'body2'}
                      className="min-w-0 truncate text-neutral"
                    >
                      {trip.originStop?.name}
                    </Typography>
                    <Icons.oneWay
                      height={20}
                      width={20}
                      className="flex-shrink-0 text-neutral"
                    />
                    <Typography
                      variant={isLaptopOrBigger ? 'paragraph' : 'body2'}
                      className="min-w-0 truncate text-neutral"
                    >
                      {trip.destinationStop?.name}
                    </Typography>
                    {fulfilledCount > 0 && (
                      <>
                        <Icons.dot className="mx-1.5 flex-shrink-0 text-neutral" />
                        <Typography
                          variant={isLaptopOrBigger ? 'paragraph' : 'body2'}
                          className="flex-shrink-0 whitespace-nowrap text-neutral"
                        >
                          <TransText
                            i18nKey="passengersCount"
                            values={{
                              count: fulfilledCount,
                            }}
                          />
                        </Typography>
                      </>
                    )}
                  </div>
                  {trip.legs?.map((leg, index) => {
                    const nextLeg = trip.legs?.[index + 1];
                    const admissions = leg.id ? admissionsByLegId[leg.id] : [];

                    return (
                      <LegCard
                        key={index}
                        leg={leg}
                        nextLeg={nextLeg}
                        admissions={admissions}
                        passengersMap={passengersMap}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}
      <SendTicketsToEmailModal
        isOpen={isSendToEmailModalOpen}
        onOpenChange={setIsSendToEmailModalOpen}
      />
    </div>
  );
};

export default TripsByDirection;
